import axios from './common';

const state = {
    reviews: [],
    reviewsQueryParams: {
        review: null,
        limit: 10,
        page: 1,
    },
    currentReviewItem: null,
    editableReviewItem: null,
    reviewsLoading: false,
    reviewItemLoading: false,
    reviewApiError: null,
    totalReviews: 0,
};

const getters = {
    reviews: state => {
        return state.reviews;
    },
    reviewsQueryParams: state => {
        return state.reviewsQueryParams;
    },
    reviewsLoading: state => {
        return state.reviewsLoading;
    },
    currentReviewItem: state => {
        return state.currentReviewItem;
    },
    editableReviewItem: state => {
        return state.editableReviewItem;
    },
    reviewItemLoading: state => {
        return state.reviewItemLoading;
    },
    reviewApiError: state => {
        return state.reviewApiError;
    },
    totalReviews: state => {
        return state.totalReviews;
    },
};

const actions = {
    getReviews(context) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: 'Loading Reviews',
        }, {root: true}).then(() => context.commit('setReviewsLoading', true));

        axios.get(`${process.env.VUE_APP_SERVICE_URI_REVIEW}/api/reviews/`, {
            params: context.state.reviewsQueryParams,
        })
            .then(response => {
                context.dispatch('addNotification', {
                    status: 'success',
                    message: 'Reviews successfully loaded',
                }, {root: true}).then(function() {
                    context.commit('setReviewsLoading', false);
                    context.commit('setReviews', response.data);
                });
            });
    },
    async deleteReview(context, item) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Deleting Review "${item.title}"`,
        }, {root: true}).then(() => context.commit('setReviewItemLoading', true));

        try {
            await axios.delete(`${process.env.VUE_APP_SERVICE_URI_REVIEW}/api/reviews/${item._id}`);
            await context.dispatch('addNotification', {
                status: 'success',
                message: `Successfully deleted Review "${item.title}"`,
            }, {root: true});
            return true;
        } catch(error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Review "${item.title}" was deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Delete failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    context.commit('setReviewApiError', error);
                    console.error('reviewApiError', error);
                });
            }
            return false;
        } finally {
            context.commit('setReviewItemLoading', false);
            await context.dispatch('clearReviewItems');
            await context.dispatch('getReviews');
        }
    },
    async getReview(context, item) {
        context.dispatch('addNotification', {
            status: 'loading',
            message: `Retrieving Review "${item.title}"`,
        }, {root: true}).then(function () {
            context.commit('setReviewItemLoading', true);
            context.commit('setReviewApiError', null);
        });

        try {
            const response = await axios.get(`${process.env.VUE_APP_SERVICE_URI_REVIEW}/api/reviews/${item._id}`);
            context.commit('setCurrentReviewItem', response.data);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Review "${item.title}" deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Retrieval failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    console.error('reviewApiError', error);
                });
            }
        } finally {
            context.commit('setReviewItemLoading', false);
        }
    },
    async editReview(context, item) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Editing Review "${item.title}"`,
        }, {root: true}).then(function() {
            context.commit('setReviewItemLoading', true);
            context.commit('setReviewApiError', null);
        });

        try {
            await axios.put(`${process.env.VUE_APP_SERVICE_URI_REVIEW}/api/reviews/${item._id}`, item);

            await context.dispatch('addNotification', {
                status: 'success',
                message: `Review "${item.title}" successfully edited `,
            }, {root: true});

            return true;

        } catch (error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Review "${item.title}" was deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Edit failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    context.commit('reviewApiError', error);
                    console.error('reviewApiError', error);
                });
            }

            return false;
        } finally {
            context.commit('setReviewItemLoading', false);
            await context.dispatch('clearReviewItems');
            await context.dispatch('getReviews');
        }
    },
    async addReview(context, item) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Adding Review "${item.title}"`,
        }, {root: true}).then(function () {
            context.commit('setReviewItemLoading', true);
            context.commit('setReviewApiError', null);
        });

        try {

            await axios.post(`${process.env.VUE_APP_SERVICE_URI_REVIEW}/api/reviews/`, item);

            await context.dispatch('addNotification', {
                status: 'success',
                message: `Review "${item.title}" successfully added`,
            }, {root: true});

            return true;

        } catch (error) {
            if (error.response) {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Failed to add Review "${item.title}", please try again`,
                }, {root: true}).then(() => {
                    context.commit('setReviewApiError', error.response.data);
                });
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Add failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    context.commit('setReviewApiError', error);
                    console.error('reviewApiError', error);
                });
            }

            return false;
        } finally {
            context.commit('setReviewItemLoading', false);
            await context.dispatch('getReviews');
        }
    },
    clearReviewItems(context) {
        context.commit('setCurrentReviewItem', null);
        context.commit('setEditableReviewItem', null);
    },
    clearReviewApiError(context) {
        context.commit('setReviewApiError', null);
    },
    setReviewPaginationOptions(context, options) {
        context.commit('reviewPaginationOptions', options);
    },
    setReviewsSearch(context, search) {
        context.commit('reviewsSearch', search);
    },
};

const mutations = {
    reviewPaginationOptions(state, options) {
        state.reviewsQueryParams.limit = options.itemsPerPage ?? 10;
        state.reviewsQueryParams.page = options.page ?? 1;
    },
    reviewsSearch(state, search) {
        state.reviewsQueryParams.review = `/${search.trim()}/i`;
    },
    setReviews(state, responseData) {
        state.reviews = responseData.docs;
        state.totalReviews = responseData.totalDocs;
    },
    setCurrentReviewItem(state, item) {
        state.currentReviewItem = item;
        state.editableReviewItem = {...item};
    },
    setEditableReviewItem(state, item) {
        state.editableReviewItem = item;
    },
    setReviewItemLoading(state, isLoading) {
        state.reviewItemLoading = isLoading;
    },
    setReviewsLoading(state, isLoading) {
        state.reviewsLoading = isLoading;
    },
    setReviewApiError(state, error) {
        state.reviewApiError = error;
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};
